import { type ReactNode } from "react";
import { Link } from "react-router";
import { useJobBomPreview } from "@/lib/bom";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";

export interface BomPreviewTableProps {
  jobId: number;
  /** Optional onAddItem; if provided, an "+ Add Item" button appears in the
   *  empty state and the table footer. Task 8 wires the picker. */
  onAddItem?: () => void;
}

/**
 * Mini-table preview of the BOM line items attached to a job.
 *
 * Pulls from `useJobBomPreview`, which is currently a no-op stub returning
 * `{ items: [] }` because BOM Plan I did not ship a "list BOMs by parent"
 * endpoint. The empty-state path is therefore the default until that
 * endpoint lands (see web/src/lib/bom.ts).
 */
export function BomPreviewTable({ jobId, onAddItem }: BomPreviewTableProps) {
  const { data, isLoading, error } = useJobBomPreview(jobId, { limit: 5 });

  // Header (title + "+ Add Item") is always rendered so the section identity is
  // stable across loading / empty / error / populated states.
  const header = (
    <div className="mb-2 flex items-center justify-between">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        BOM preview
      </h3>
      {onAddItem && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-slate-500 hover:text-slate-800"
          onClick={onAddItem}
        >
          <Plus className="h-3.5 w-3.5" /> Add Item
        </Button>
      )}
    </div>
  );

  let body: ReactNode;
  if (isLoading) {
    body = <p className="text-sm text-muted-foreground">Loading BOM…</p>;
  } else if (error) {
    body = <p className="text-sm text-destructive">Failed to load BOM preview.</p>;
  } else if (!data || data.items.length === 0) {
    body = <p className="text-sm text-muted-foreground">No BOM items yet</p>;
  } else {
    body = (
      <div className="rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-left">
            <tr>
              <th scope="col" className="w-12 px-3 py-2">#</th>
              <th scope="col" className="px-3 py-2">Description</th>
              <th scope="col" className="w-16 px-3 py-2 text-right">Qty</th>
              <th scope="col" className="w-24 px-3 py-2 text-right">Price</th>
            </tr>
          </thead>
        <tbody>
          {data.items.slice(0, 5).map((item, i) => (
            <tr key={item.id} className="border-t border-border">
              <td className="px-3 py-2">{i + 1}</td>
              <td className="px-3 py-2">{item.description}</td>
              <td className="px-3 py-2 text-right">{item.quantity}</td>
              <td className="px-3 py-2 text-right">${item.unitPrice.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
        {data.items.length > 5 && (
          <div className="border-t border-border px-3 py-2 text-right text-sm">
            <Link to={`/jobs/${jobId}/bom`} className="text-primary hover:underline">
              View all ({data.items.length})
            </Link>
          </div>
        )}
      </div>
    );
  }

  return (
    <section className="rounded-lg border border-border bg-card p-3">
      {header}
      {body}
    </section>
  );
}
